import { useQuery } from "@apollo/client/react";
import { XOR } from "ts-essentials";
import { Asset, Contract, ContractSignatureStatus, MissionAmendment } from "../__generated__/graphql";
import { GetAmendmentQuery, GetContractQuery } from "../__generated__/operations/graphql";
import { GET_CONTRACT } from "../graphql/core";
import { GET_AMENDMENT } from "../graphql/amendment";
import { ContractType } from "../types/contractType";
import { CalloutMode } from "../ui_atoms/Callout";
import { AuthManagerContext } from "../contexts/AuthManager";
import { use } from "react";

export type ContractOrAmendmentOrClientFramework = XOR<
    XOR<Contract, MissionAmendment>,
    { signatureStatus: ContractSignatureStatus; clientEffectiveAsset?: Asset }
>;

export const useContract = (
    contractId: string,
    contractType: ContractType,
    messageInfoMode?: CalloutMode | null,
) => {
    const { user } = use(AuthManagerContext);

    const { data, error, loading } = useQuery<XOR<GetContractQuery, GetAmendmentQuery>>(
        contractType === "contract" ? GET_CONTRACT : GET_AMENDMENT,
        {
            variables: { id: contractId },
            pollInterval: messageInfoMode === "error" ? 10000 : 0,
            skip: contractType === "client_framework",
        },
    );

    const clientFrameworkClient =
        contractType === "client_framework"
            ? user?.clientProfiles?.find((clientProfile) => clientProfile.client.id === contractId)
                  ?.client
            : undefined;

    const contract = (data?.getContract ||
        data?.getAmendment || {
            signatureStatus: clientFrameworkClient?.signatureStatus,
            clientEffectiveAsset: clientFrameworkClient?.contractEffectiveAsset,
        }) as ContractOrAmendmentOrClientFramework;

    return {
        contract,
        asset:
            contract.contractEffectiveAsset ||
            contract.amendmentEffectiveAsset ||
            contract.clientEffectiveAsset,
        error,
        loading,
    };
};
